import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  Toolbar,
  Divider,
  IconButton,
  Button,
  Avatar,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import useAuthentication from "../hooks/useAuthentication";
import { Menu } from "@mui/icons-material";
import CssBaseline from "@mui/material/CssBaseline";
import MenuSkeleton from "./MenuSkeleton";
import SettingsComponent from "./SettingsComponent";
import insuranceRouteList from "../Insurance/routeList";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { assetsUrl, baseURL } from "../constants";
import usePopup from "../hooks/usePopup";
import Wallet from "../MainDashboard/Wallet/Wallet";
import PageRouterList from "../routes/routeList";
import moduleRoutes from "../routes/moduleRouteList";
import Notification from "./Notification";
import { AiWalletProvider } from "../context/AiWallet/AiWalletContext";
import { VoiceAssistantProvider } from "../context/VoiceAssistant/VoiceAssistantContext";
import GlobalVoiceAssistantFab from "../components/VoiceAssistant/GlobalVoiceAssistantFab";
import GlobalAiAssistantDrawer from "../components/VoiceAssistant/GlobalAiAssistantDrawer";
import VoiceCommandHandler from "../components/VoiceAssistant/VoiceCommandHandler";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { hasAiAccess } from "../utilis/methods/aiAccess";
import {
  GlobalMessengerDrawer,
  MessengerHeaderButton,
  MessengerProvider,
} from "../Messenger";
// ----------------- icons ------------------
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import CloseIcon from "@mui/icons-material/Close";
import { getRegionByTimezone } from "../utilis/methods/region";
const SideBarComponent = lazy(() => import("./SideBarComponent"));
const drawerWidth = 18;

const openedMixin = (theme: Theme): CSSObject => ({
  width: `${drawerWidth}vw`,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2.6, 1),
  backgroundColor: theme.palette.sideBarColor.dark,
}));

const MenuContainer = styled("div")(({ theme }) => ({
  minHeight: "92%",
  maxHeight: "100%",
  overflowY: "scroll",
  backgroundColor: theme.palette.sideBarColor.main,
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#5f6386",
    borderRadius: "8px",
    opacity: 1,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.sideBarColor.main,
  },
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: `${drawerWidth}vw`,
    width: `calc(100% - ${drawerWidth}vw)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: `${drawerWidth}vw`,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function MainLayoutContainer() {
  const location = useLocation();
  const theme = useTheme();
  const { CustomModal } = usePopup();
  const navigateTo = useNavigate();
  const { logout, user } = useAuthentication();
  const allowAi = hasAiAccess(user);
  const [open, setSideBarOpen] = useState(true);
  const [isOpenClicked, setIsOpenclicked] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [show, setShow] = useState(false);
  const [shouldShowWallet, setShouldShowWallet] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerOpen = () => {
    setIsOpenclicked(false);
    if (!open) {
      setSideBarOpen(true);
    }
  };

  const handleDrawerClose = () => {
    setIsOpenclicked(true);
    if (open) {
      setSideBarOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigateTo(`${moduleRoutes.root}${moduleRoutes.authentication}`, {
      replace: true,
    });
  };

  const handleOpenTooltip = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseTooltip = () => {
    setAnchorEl(null);
  };

  const handleHelpClick = () => {
    window.location.href = `${baseURL}#/help`;
  };

  useEffect(() => {
    try {
      const regx = new RegExp(
        `/${moduleRoutes.patientCare}/${PageRouterList.payment}/\\d+/\\d+/\\d+`,
      );
      setShouldShowWallet(regx.test(location.pathname));
      const splited = location.pathname.split("/");
      if (splited[splited.length - 1] == insuranceRouteList.Verification) {
        setIsOpenclicked(true);
        setSideBarOpen(false);
      }
    } catch (error) {
      console.log(error);
    }
  }, [location.pathname]);

  const TooltipComponent = () => {
    return (
      <div>
        <div className="flex flex-col p-4 gap-5 h-fit">
          <div className="flex justify-between">
            <strong className="text-sm">ACCOUNT</strong>
          </div>
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-3">
              <Avatar
                color="primary"
                src={user?.photo || ""}
                sx={{ width: 50, height: 50 }}
              />
            </div>
            <div className="col-span-9 flex flex-col gap-1">
              <span className="text-base">{user?.emp_name}</span>
              <span className="text-xs">{user?.username}</span>
            </div>
          </div>
        </div>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setOpenSettings((prev) => !prev);
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
            <Divider />
          </ListItem>
        </List>
        <div className="p-3 flex justify-end">
          <Button
            color="error"
            size="small"
            endIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            logout
          </Button>
        </div>
      </div>
    );
  };

  const openWallet = () => {
    setShow(true);
  };

  return (
    <AiWalletProvider>
    <VoiceAssistantProvider>
    <MessengerProvider>
      <>
      <CssBaseline />
      <Box sx={{ display: "flex" }}>
        <AppBar position="fixed" open={open}>
          <Toolbar
            variant="dense"
            sx={{ backgroundColor: theme.palette.background.paper }}
            className="flex flex-row justify-between items-center"
          >
            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: "none" }),
              }}
            >
              <Menu />
            </IconButton>
            <div className="w-full flex justify-between items-center gap-2">
              <div></div>
              <div className="flex gap-1">
                {getRegionByTimezone() == "QA" && (
                  <img
                    alt="Netdent Logo"
                    style={{ height: "54px", width: "112px" }}
                    src={`${assetsUrl}/logos/NETDENT.png`}
                  />
                )}
                {shouldShowWallet && (
                  <IconButton onClick={openWallet}>
                    <AccountBalanceWalletIcon
                      color="secondary"
                      sx={{ fontSize: "2rem" }}
                    />
                  </IconButton>
                )}
                <MessengerHeaderButton />
                <Notification />
                {allowAi && (
                  <IconButton
                    aria-label="Open AI assistant"
                    onClick={() =>
                      window.dispatchEvent(new CustomEvent("rcm-ai-drawer-toggle"))
                    }
                    color="primary"
                  >
                    <AutoAwesomeIcon sx={{ fontSize: "1.75rem" }} />
                  </IconButton>
                )}
                <>
                  <IconButton>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.primary.main,
                      }}
                      src={user?.photo}
                      onClick={handleOpenTooltip}
                    />
                  </IconButton>
                  <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleCloseTooltip}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    PaperProps={{
                      sx: {
                        width: 320,
                        padding: 0,
                      },
                    }}
                  >
                    <TooltipComponent />
                  </Popover>
                </>
                {/* </HtmlTooltip> */}
                <IconButton onClick={handleHelpClick}>
                  <QuestionMarkIcon
                    sx={{ fontSize: "2.1rem" }}
                    className="text-violet-700"
                  />
                </IconButton>
              </div>
            </div>
          </Toolbar>
        </AppBar>
        <Drawer
          onMouseLeave={() => {
            isOpenClicked && setSideBarOpen(false);
          }}
          onMouseEnter={() => {
            isOpenClicked && setSideBarOpen(true);
          }}
          variant="permanent"
          open={open}
        >
          <DrawerHeader variant="dense">
            <section>
              {/* {getRegionByTimezone() == "QA" ? <img height={15} width={112} src={`${assetsUrl}/logos/NETDENT.png`} alt="" /> : <img src={`${assetsUrl}/logos/logo_pappyjoe.png`} alt="" />}
               */}
              <img src={`${assetsUrl}/logos/logo_pappyjoe.png`} alt="" />
            </section>
            <IconButton
              onClick={handleDrawerClose}
              sx={(theme) => ({
                color: theme.palette.sideBarColor.contrastText,
              })}
            >
              <Menu />
            </IconButton>
          </DrawerHeader>
          <MenuContainer>
            <Suspense fallback={<MenuSkeleton />}>
              <SideBarComponent isOpen={open} />
            </Suspense>
          </MenuContainer>
        </Drawer>
        <MuiDrawer anchor="right" open={openSettings}>
          <Box sx={{ width: `${drawerWidth}vw` }}>
            <DrawerHeader variant="dense" />
            <div className="flex justify-end">
              <IconButton
                color="error"
                size="small"
                onClick={() => setOpenSettings(false)}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <SettingsComponent />
          </Box>
        </MuiDrawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            backgroundColor: theme.palette.background.default,
            height: "100dvh",
          }}
        >
          <DrawerHeader variant="dense" />
          <section>
            <Outlet context={{ open, setSideBarOpen }} />
          </section>
        </Box>
      </Box>
      <CustomModal title="" open={show} setOpen={setShow}>
        <Wallet />
      </CustomModal>
      {allowAi && (
        <>
          <GlobalVoiceAssistantFab />
          <GlobalAiAssistantDrawer />
          <VoiceCommandHandler />
        </>
      )}
      <GlobalMessengerDrawer />
      </>
    </MessengerProvider>
    </VoiceAssistantProvider>
    </AiWalletProvider>
  );
}

export default MainLayoutContainer;
